import React, { useState, useEffect } from 'react'
import { Card, Row, Col, Avatar, Rate, Button, Select, Typography, Space, Divider, Modal, Tag, Spin, theme } from 'antd'
import {
  UserOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
  SafetyCertificateOutlined,
  HeatMapOutlined,
  ArrowLeftOutlined,
  LeftOutlined,
  RightOutlined,
  CalendarOutlined,
  ThunderboltOutlined
} from '@ant-design/icons'
import { getDoctorProfile, getDoctorAvailability } from '../api/searchApi'
import { hexToRGBA } from '../../lib/utils'
import AppointmentConfirmation from './AppointmentConfirmation'

const { Title, Text, Paragraph } = Typography
const { Option } = Select

const DoctorAvailabilityView = ({ doctor, onClose, onBookAppointment }) => {
  const { token } = theme.useToken() // Move hook to top before any returns
  const [loading, setLoading] = useState(false)
  const [showAvailability, setShowAvailability] = useState(!loading)
  const [loadingStartTime, setLoadingStartTime] = useState(null)
  const [selectedReason, setSelectedReason] = useState(null)
  const [selectedSlot, setSelectedSlot] = useState(null)
  const [timeSlots, setTimeSlots] = useState([])
  const [showReasonModal, setShowReasonModal] = useState(false)
  const [currentWeekStart, setCurrentWeekStart] = useState(new Date())
  const [availabilityData, setAvailabilityData] = useState(null)
  const [currentView, setCurrentView] = useState('calendar') // 'calendar' or 'confirmation'
  const [hasInitializedWeek, setHasInitializedWeek] = useState(false)

  useEffect(() => {
    fetchAvailability()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctor, currentWeekStart])

  // Track loading timing to ensure minimum duration
  useEffect(() => {
    if (loading) {
      setLoadingStartTime(Date.now())
      setShowAvailability(false)
    } else if (loadingStartTime) {
      // Ensure minimum loading time of 2 seconds for full animation
      const elapsed = Date.now() - loadingStartTime
      const minimumLoadingTime = 2000 // 2 seconds

      if (elapsed < minimumLoadingTime) {
        const remainingTime = minimumLoadingTime - elapsed
        setTimeout(() => {
          setShowAvailability(true)
        }, remainingTime)
      } else {
        setShowAvailability(true)
      }
    }
  }, [loading, loadingStartTime])

  const fetchAvailability = async () => {
    if (!doctor) return

    setLoading(true)
    try {
      // Get the current week's date range
      const weekDates = getWeekDates(currentWeekStart)
      const startDate = weekDates[0].toISOString().split('T')[0] // YYYY-MM-DD format
      const endDate = weekDates[6].toISOString().split('T')[0] // YYYY-MM-DD format

      const filter = {
        medicalProfessional: doctor._id,
        clinicRef: doctor.clinicRecord?._id
      }

      const availability = await getDoctorAvailability(filter)
      setAvailabilityData(availability)

      // Auto-navigate to the week containing the earliest available appointment (only on initial load)
      if (!hasInitializedWeek && availability?.availability?.length > 0) {
        // Get today's date for comparison
        const today = new Date()
        const todayStr = today.toISOString().split('T')[0]

        // Find the earliest date with available time slots that is not in the past
        const earliestDate = availability.availability
          .filter((dayAvail) => dayAvail.date >= todayStr && dayAvail.timeSlots && dayAvail.timeSlots.length > 0)
          .map((dayAvail) => new Date(dayAvail.date))
          .sort((a, b) => a - b)[0] // Get earliest date

        if (earliestDate) {
          setCurrentWeekStart(getMonday(earliestDate))
          setHasInitializedWeek(true)
        } else {
          // If no future availability found, start from current week
          setCurrentWeekStart(getMonday(today))
          setHasInitializedWeek(true)
        }
      }
    } catch (error) {
      console.error('Error fetching availability:', error)
      setAvailabilityData(null)
    } finally {
      setLoading(false)
    }
  }

  // Get Monday of current week
  const getMonday = (date) => {
    const d = new Date(date)
    const day = d.getDay()
    const diff = d.getDate() - day + (day === 0 ? -6 : 1) // adjust when day is sunday
    return new Date(d.setDate(diff))
  }

  // Get week dates starting from Monday
  const getWeekDates = (startDate) => {
    const dates = []
    const monday = getMonday(startDate)

    for (let i = 0; i < 7; i++) {
      const date = new Date(monday)
      date.setDate(monday.getDate() + i)
      dates.push(date)
    }
    return dates
  }

  // Format date for display
  const formatDate = (date) => {
    const dayNames = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT']
    const day = dayNames[date.getDay()]
    const dayNum = date.getDate()
    const month = date.getMonth() + 1
    return { day, dayNum, month, fullDate: `${day} ${dayNum}/${month}` }
  }

  // Generate time slots for the full week using real availability data
  const generateTimeSlots = () => {
    // Get all unique time slots from availability data
    const allTimes = new Set()

    // Get today's date in YYYY-MM-DD format for comparison
    const today = new Date()
    const todayStr = today.toISOString().split('T')[0]

    if (availabilityData?.availability) {
      // Filter out past days and only process current and future days
      const filteredAvailability = availabilityData.availability.filter((dayAvailability) => {
        return dayAvailability.date >= todayStr
      })

      filteredAvailability.forEach((dayAvailability) => {
        dayAvailability.timeSlots.forEach((slot) => {
          allTimes.add(slot.time)
        })
      })
    }

    // Convert to sorted array of times
    const times = Array.from(allTimes).sort()

    // If no availability data, show default time range
    if (times.length === 0) {
      const defaultTimes = [
        '08:00',
        '08:15',
        '08:30',
        '08:45',
        '09:00',
        '09:15',
        '09:30',
        '09:45',
        '10:00',
        '10:15',
        '10:30',
        '10:45',
        '11:00',
        '11:15',
        '11:30',
        '11:45',
        '12:00',
        '12:15',
        '12:30',
        '12:45',
        '13:00',
        '13:15',
        '13:30',
        '13:45',
        '14:00',
        '14:15',
        '14:30',
        '14:45',
        '15:00',
        '15:15',
        '15:30',
        '15:45'
      ]
      times.push(...defaultTimes)
    }

    const weekDates = getWeekDates(currentWeekStart)

    return times.map((time) => ({
      time,
      slots: weekDates.map((date, dayIndex) => {
        const dateStr = date.toISOString().split('T')[0] // YYYY-MM-DD format

        // Get today's date for comparison
        const today = new Date()
        const todayStr = today.toISOString().split('T')[0]

        // If this date is before today, mark as unavailable
        if (dateStr < todayStr) {
          return {
            date,
            dayIndex,
            time,
            available: false,
            id: `${dayIndex}-${time}`,
            slotId: null
          }
        }

        // Find availability for this specific date
        const dayAvailability = availabilityData?.availability?.find((avail) => avail.date === dateStr)

        // Find specific time slot for this date and time
        const timeSlot = dayAvailability?.timeSlots?.find((slot) => slot.time === time)

        return {
          date,
          dayIndex,
          time,
          available: timeSlot ? !timeSlot.blocked : false,
          id: `${dayIndex}-${time}`,
          slotId: timeSlot?._id
        }
      })
    }))
  }

  // Navigate weeks
  const navigateWeek = (direction) => {
    const newDate = new Date(currentWeekStart)
    newDate.setDate(newDate.getDate() + direction * 7)

    // Prevent going back to past weeks
    if (direction < 0) {
      const today = new Date()
      const currentWeekMonday = getMonday(today)

      // Don't allow navigation to weeks before the current week
      if (newDate < currentWeekMonday) {
        return // Block navigation to past weeks
      }
    }

    setCurrentWeekStart(newDate)
    // Availability will be refetched automatically due to useEffect dependency
  }

  // Check if we can navigate backwards (not on current week or past week)
  const canNavigateBack = () => {
    const today = new Date()
    const currentWeekMonday = getMonday(today)
    const previousWeek = new Date(currentWeekStart)
    previousWeek.setDate(previousWeek.getDate() - 7)

    return previousWeek >= currentWeekMonday
  }

  const appointmentReasons = [
    'General Consultation',
    'Follow-up Appointment',
    'Medical Certificate',
    'Prescription Renewal',
    'Mental Health Consultation',
    'Therapy Session',
    'Assessment',
    'Other'
  ]

  useEffect(() => {
    // Reset initialization flag and week when doctor changes
    setHasInitializedWeek(false)

    // Initialize with the week containing the doctor's earliest available appointment
    if (doctor?.nextAvailableSlot?.date) {
      // Use the doctor's next available slot date, but ensure it's not in the past
      const nextAvailableDate = new Date(doctor.nextAvailableSlot.date)
      const today = new Date()

      // Only use the next available slot if it's today or in the future
      if (nextAvailableDate >= today) {
        setCurrentWeekStart(getMonday(nextAvailableDate))
        setHasInitializedWeek(true)
      } else {
        // If the next available slot is in the past, start from current week
        setCurrentWeekStart(getMonday(today))
        setHasInitializedWeek(true)
      }
    } else {
      // Fallback to current week if no next available slot (will be updated by fetchAvailability)
      const today = new Date()
      setCurrentWeekStart(getMonday(today))
    }
  }, [doctor])

  useEffect(() => {
    // Regenerate time slots when week changes or availability data changes
    setTimeSlots(generateTimeSlots())
  }, [currentWeekStart, availabilityData])

  const handleSlotClick = (slot) => {
    if (!selectedReason) {
      setShowReasonModal(true)
      return
    }
    setSelectedSlot(slot)
    setCurrentView('confirmation')
  }

  const handleBooking = () => {
    if (selectedSlot && selectedReason) {
      const formattedDate = formatDate(selectedSlot.date)

      onBookAppointment?.({
        doctorId: doctor._id,
        slot: selectedSlot,
        reason: selectedReason,
        datetime: `${formattedDate.fullDate} ${selectedSlot.time}`
      })
    }
  }

  const handleBackToCalendar = () => {
    setCurrentView('calendar')
    setSelectedSlot(null)
  }

  const handleConfirmBooking = async (bookingData) => {
    // Call the parent's booking handler
    await onBookAppointment?.(bookingData)
  }

  const getDoctorName = () => {
    if (!doctor) return ''
    return `${doctor.firstName} ${doctor.lastName}`.trim()
  }

  if (!doctor) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <Text>Doctor not found</Text>
      </div>
    )
  }

  return (
    <div
      style={{
        padding: '0',
        background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
        minHeight: '100vh'
      }}
    >
      {/* Back Button */}
      <div style={{ padding: '20px 20px 0 20px', marginBottom: '20px' }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={onClose}
          style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
            borderColor: 'transparent',
            color: 'white',
            fontWeight: '600',
            height: '44px',
            borderRadius: '12px',
            fontSize: '15px',
            boxShadow: '0 4px 16px rgba(59, 130, 246, 0.3)',
            padding: '0 20px'
          }}
        >
          Back to Search Results
        </Button>
      </div>

      <div style={{ padding: '0 20px 20px 20px' }}>
        <Row gutter={[24, 24]} style={{ minHeight: 'calc(100vh - 100px)' }}>
          {/* Doctor Profile Section */}
          <Col xs={24} lg={8}>
            <Card
              style={{
                height: '100%',
                borderRadius: '16px',
                border: 'none',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)'
              }}
            >
              <div style={{ position: 'relative' }}>
                {/* Modern checkmark badge */}
                <div
                  style={{
                    position: 'absolute',
                    top: '-10px',
                    right: '-10px',
                    width: '32px',
                    height: '32px',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '16px',
                    zIndex: 1,
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)',
                    border: '2px solid white'
                  }}
                >
                  ✓
                </div>

                <Avatar
                  size={150}
                  src={doctor.image || '/api/placeholder/150/150'}
                  icon={<UserOutlined />}
                  style={{
                    display: 'block',
                    margin: '0 auto 20px auto',
                    border: '4px solid rgba(59, 130, 246, 0.1)',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)'
                  }}
                />
              </div>

              <Title
                level={3}
                style={{
                  textAlign: 'center',
                  marginBottom: '8px',
                  color: '#1f2937',
                  fontWeight: '600',
                  fontSize: '24px'
                }}
              >
                {getDoctorName()}
              </Title>

              <Title
                level={4}
                style={{
                  textAlign: 'center',
                  color: '#6b7280',
                  fontWeight: '500',
                  marginBottom: '8px',
                  fontSize: '16px'
                }}
              >
                {doctor.specialty || doctor.extraData?.qualification || 'Medical Practitioner'}
              </Title>

              <Text
                style={{
                  display: 'block',
                  textAlign: 'center',
                  color: '#9ca3af',
                  marginBottom: '20px',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                {doctor.practice || doctor.clinicRecord?.name || 'Medical Practice'}
              </Text>

              <div
                style={{
                  textAlign: 'center',
                  marginBottom: '24px',
                  padding: '16px',
                  background: 'rgba(59, 130, 246, 0.05)',
                  borderRadius: '12px',
                  border: '1px solid rgba(59, 130, 246, 0.1)'
                }}
              >
                <Rate disabled value={doctor.rating || 4.5} style={{ fontSize: '18px', color: '#fbbf24' }} />
                <Text
                  style={{
                    marginLeft: '8px',
                    color: '#374151',
                    fontWeight: '500',
                    fontSize: '14px'
                  }}
                >
                  ({doctor.reviewCount || 0} recommendations)
                </Text>
              </div>

              {/* Location */}
              <div
                style={{
                  marginBottom: '20px',
                  padding: '16px',
                  background: 'rgba(239, 68, 68, 0.05)',
                  borderRadius: '12px',
                  border: '1px solid rgba(239, 68, 68, 0.1)'
                }}
              >
                <Space align='start' style={{ width: '100%' }}>
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    <EnvironmentOutlined style={{ color: 'white', fontSize: '18px' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <Text strong style={{ display: 'block', marginBottom: '6px', color: '#1f2937', fontSize: '15px' }}>
                      {doctor.clinicRecord?.name || getDoctorName()}
                    </Text>
                    <Text style={{ display: 'block', fontSize: '13px', color: '#6b7280', lineHeight: '1.4' }}>
                      {doctor.clinicRecord?.address || doctor.clinicRecord?.physicalAddress || 'Address not available'}
                    </Text>
                    {doctor.location && (
                      <Text style={{ display: 'block', fontSize: '13px', color: '#6b7280', lineHeight: '1.4' }}>
                        {doctor.location}
                      </Text>
                    )}
                  </div>
                </Space>
              </div>

              {/* Opening Hours */}
              <div
                style={{
                  marginBottom: '20px',
                  padding: '16px',
                  background: 'rgba(34, 197, 94, 0.05)',
                  borderRadius: '12px',
                  border: '1px solid rgba(34, 197, 94, 0.1)'
                }}
              >
                <Space align='start' style={{ width: '100%' }}>
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    <ClockCircleOutlined style={{ color: 'white', fontSize: '18px' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <Text strong style={{ display: 'block', marginBottom: '6px', color: '#1f2937', fontSize: '15px' }}>
                      Opening Hours
                    </Text>
                    <Text style={{ display: 'block', fontSize: '13px', color: '#6b7280', lineHeight: '1.4' }}>
                      09:00 to 15:00 (Mon, Wed and Thurs)
                    </Text>
                    <Text style={{ display: 'block', fontSize: '13px', color: '#6b7280', lineHeight: '1.4' }}>
                      09:00 to 12:00 (Tues and Fri)
                    </Text>
                  </div>
                </Space>
              </div>

              {/* Qualifications */}
              <div
                style={{
                  marginBottom: '20px',
                  padding: '16px',
                  background: 'rgba(168, 85, 247, 0.05)',
                  borderRadius: '12px',
                  border: '1px solid rgba(168, 85, 247, 0.1)'
                }}
              >
                <Space align='start' style={{ width: '100%' }}>
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      background: 'linear-gradient(135deg, #a855f7 0%, #9333ea 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    <SafetyCertificateOutlined style={{ color: 'white', fontSize: '18px' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <Text strong style={{ display: 'block', marginBottom: '6px', color: '#1f2937', fontSize: '15px' }}>
                      Qualifications
                    </Text>
                    <Text style={{ display: 'block', fontSize: '13px', color: '#6b7280', lineHeight: '1.4' }}>
                      {doctor.extraData?.qualification || 'Medical Practitioner'}
                    </Text>
                    {doctor.extraData?.mpNumber && (
                      <Text style={{ display: 'block', fontSize: '13px', color: '#6b7280', lineHeight: '1.4' }}>
                        MP Number: {doctor.extraData.mpNumber}
                      </Text>
                    )}
                  </div>
                </Space>
              </div>
            </Card>
          </Col>

          {/* Appointment Booking Section */}
          <Col xs={24} lg={16}>
            {/* Show confirmation inline if in confirmation view */}
            {currentView === 'confirmation' ? (
              <AppointmentConfirmation
                doctor={doctor}
                setCurrentView={setCurrentView}
                selectedSlot={selectedSlot}
                selectedReason={selectedReason}
                onBack={handleBackToCalendar}
                onClose={onClose}
                onConfirmBooking={handleConfirmBooking}
                inline={true}
              />
            ) : loading || !showAvailability ? (
              /* Beautiful Loading Animation */
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '60px 20px',
                  minHeight: '500px',
                  background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                  borderRadius: '16px'
                }}
              >
                {/* Main Loading Animation */}
                <div
                  style={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '32px'
                  }}
                >
                  {/* Outer Rotating Ring */}
                  <div
                    style={{
                      width: '80px',
                      height: '80px',
                      border: '3px solid rgba(59, 130, 246, 0.2)',
                      borderTop: '3px solid #3b82f6',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      position: 'absolute'
                    }}
                  />

                  {/* Inner Pulsing Circle */}
                  <div
                    style={{
                      width: '60px',
                      height: '60px',
                      background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      animation: 'pulse 2s infinite',
                      boxShadow: '0 8px 32px rgba(59, 130, 246, 0.4)'
                    }}
                  >
                    <CalendarOutlined style={{ fontSize: '24px', color: 'white' }} />
                  </div>
                </div>

                {/* Loading Text with Gradient */}
                <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                  <Title
                    level={3}
                    style={{
                      background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      margin: '0 0 8px 0',
                      fontWeight: '600'
                    }}
                  >
                    Loading Availability
                  </Title>
                  <Text style={{ color: '#6b7280', fontSize: '16px' }}>
                    Checking {getDoctorName()}'s appointment schedule...
                  </Text>
                </div>

                {/* Availability Progress Indicators */}
                <div
                  style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%', maxWidth: '400px' }}
                >
                  {[
                    { icon: <CalendarOutlined />, text: 'Fetching calendar data...', delay: '0s' },
                    { icon: <ClockCircleOutlined />, text: 'Checking time slots...', delay: '0.5s' },
                    { icon: <UserOutlined />, text: 'Verifying availability...', delay: '1s' },
                    { icon: <ThunderboltOutlined />, text: 'Preparing your view...', delay: '1.5s' }
                  ].map((step, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px 16px',
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        borderRadius: '8px',
                        backdropFilter: 'blur(10px)',
                        animation: `fadeInUp 0.6s ease-out ${step.delay} both`,
                        border: '1px solid rgba(255, 255, 255, 0.3)'
                      }}
                    >
                      <div
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '14px',
                          animation: 'bounce 2s infinite'
                        }}
                      >
                        {step.icon}
                      </div>
                      <Text style={{ color: '#374151', fontSize: '14px', fontWeight: '500' }}>{step.text}</Text>
                      <div style={{ marginLeft: 'auto' }}>
                        <div
                          style={{
                            width: '16px',
                            height: '16px',
                            border: '2px solid rgba(59, 130, 246, 0.2)',
                            borderTop: '2px solid #3b82f6',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Add CSS animations */}
                <style>{`
                  @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                  
                  @keyframes pulse {
                    0%, 100% { transform: scale(1); }
                    50% { transform: scale(1.1); }
                  }
                  
                  @keyframes fadeInUp {
                    from {
                      opacity: 0;
                      transform: translateY(20px);
                    }
                    to {
                      opacity: 1;
                      transform: translateY(0);
                    }
                  }
                  
                  @keyframes bounce {
                    0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
                    40% { transform: translateY(-4px); }
                    60% { transform: translateY(-2px); }
                  }
                `}</style>
              </div>
            ) : (
              <div
                style={{
                  background: 'rgba(255, 255, 255, 0.95)',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  border: 'none',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                  backdropFilter: 'blur(10px)'
                }}
              >
                {/* Week Navigation Header */}
                <div
                  style={{
                    background: 'linear-gradient(135deg, #1f2937 0%, #374151 100%)',
                    color: 'white',
                    padding: '20px 24px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <Text style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '16px', fontWeight: '500' }}>
                    Showing Times For:
                  </Text>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <Button
                      type='text'
                      icon={<LeftOutlined />}
                      onClick={() => navigateWeek(-1)}
                      disabled={!canNavigateBack()}
                      style={{
                        color: canNavigateBack() ? 'white' : 'rgba(255, 255, 255, 0.3)',
                        border: canNavigateBack() ? '1px solid rgba(255, 255, 255, 0.2)' : 'none',
                        borderRadius: '8px',
                        height: '36px',
                        width: '36px',
                        cursor: canNavigateBack() ? 'pointer' : 'not-allowed',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (canNavigateBack()) {
                          e.target.style.background = 'rgba(255, 255, 255, 0.1)'
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (canNavigateBack()) {
                          e.target.style.background = 'transparent'
                        }
                      }}
                    />
                    <Text
                      style={{
                        color: 'white',
                        fontSize: '17px',
                        fontWeight: '600',
                        minWidth: '200px',
                        textAlign: 'center'
                      }}
                    >
                      {(() => {
                        const weekDates = getWeekDates(currentWeekStart)
                        const start = formatDate(weekDates[0])
                        const end = formatDate(weekDates[6])
                        return `${start.dayNum} ${
                          ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][
                            weekDates[0].getMonth()
                          ]
                        } - ${end.dayNum} ${
                          ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][
                            weekDates[6].getMonth()
                          ]
                        }`
                      })()}
                    </Text>
                    <Button
                      type='text'
                      icon={<RightOutlined />}
                      onClick={() => navigateWeek(1)}
                      style={{
                        color: 'white',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '8px',
                        height: '36px',
                        width: '36px',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = 'rgba(255, 255, 255, 0.1)'
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'transparent'
                      }}
                    />
                  </div>
                </div>

                {/* Appointment Reason Dropdown */}
                <div
                  style={{
                    padding: '24px',
                    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                    borderBottom: '1px solid rgba(226, 232, 240, 0.8)'
                  }}
                >
                  <Text
                    strong
                    style={{
                      display: 'block',
                      marginBottom: '12px',
                      color: '#1f2937',
                      fontSize: '15px'
                    }}
                  >
                    Select Appointment Reason
                  </Text>
                  <Select
                    placeholder='Choose the reason for your visit...'
                    style={{
                      width: '100%',
                      height: '52px'
                    }}
                    size='large'
                    value={selectedReason}
                    onChange={setSelectedReason}
                  >
                    {appointmentReasons.map((reason) => (
                      <Option key={reason} value={reason}>
                        {reason}
                      </Option>
                    ))}
                  </Select>
                </div>

                {/* Calendar Grid */}
                <div style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)', padding: '16px' }}>
                  {/* Days Header */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '90px repeat(7, 1fr)',
                      gap: '6px',
                      marginBottom: '12px'
                    }}
                  >
                    <div style={{ height: '52px' }}></div> {/* Empty corner */}
                    {getWeekDates(currentWeekStart).map((date, index) => {
                      const formatted = formatDate(date)
                      return (
                        <div
                          key={index}
                          style={{
                            background: 'linear-gradient(135deg, #1f2937 0%, #374151 100%)',
                            color: 'white',
                            padding: '12px 8px',
                            textAlign: 'center',
                            fontWeight: '600',
                            fontSize: '13px',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                          }}
                        >
                          {formatted.day.slice(0, 3)} {formatted.dayNum}/{formatted.month}
                        </div>
                      )
                    })}
                  </div>

                  {/* Time Slots Grid */}
                  {timeSlots.map((timeRow, rowIndex) => (
                    <div
                      key={timeRow.time}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '90px repeat(7, 1fr)',
                        gap: '6px',
                        marginBottom: '6px'
                      }}
                    >
                      {/* Time label */}
                      <div
                        style={{
                          background: 'linear-gradient(135deg, #1f2937 0%, #374151 100%)',
                          color: 'white',
                          padding: '12px 8px',
                          textAlign: 'center',
                          fontWeight: '600',
                          fontSize: '13px',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                          height: '52px'
                        }}
                      >
                        {timeRow.time}
                      </div>

                      {/* Day slots */}
                      {timeRow.slots.map((slot) => (
                        <Button
                          key={slot.id}
                          style={{
                            background: slot.available
                              ? 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)'
                              : 'linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%)',
                            border: slot.available
                              ? '1px solid rgba(59, 130, 246, 0.3)'
                              : '1px solid rgba(209, 213, 219, 0.5)',
                            color: slot.available ? 'white' : '#9ca3af',
                            height: '52px',
                            borderRadius: '8px',
                            fontSize: '13px',
                            fontWeight: '600',
                            cursor: slot.available ? 'pointer' : 'not-allowed',
                            width: '100%',
                            transition: 'all 0.3s ease',
                            boxShadow: slot.available
                              ? '0 2px 8px rgba(59, 130, 246, 0.2)'
                              : '0 1px 3px rgba(0, 0, 0, 0.1)'
                          }}
                          className={slot.available ? 'time-slot-available' : 'time-slot-disabled'}
                          disabled={!slot.available}
                          onClick={() => handleSlotClick(slot)}
                          onMouseEnter={(e) => {
                            if (slot.available) {
                              e.target.style.background = 'linear-gradient(135deg, #2563eb 0%, #0891b2 100%)'
                              e.target.style.transform = 'translateY(-1px)'
                              e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)'
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (slot.available) {
                              e.target.style.background = 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)'
                              e.target.style.transform = 'translateY(0)'
                              e.target.style.boxShadow = '0 2px 8px rgba(59, 130, 246, 0.2)'
                            }
                          }}
                        >
                          {timeRow.time}
                        </Button>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Col>
        </Row>
      </div>

      {/* Reason Selection Modal */}
      <Modal
        title='Choose an Appointment Reason First'
        open={showReasonModal}
        onCancel={() => setShowReasonModal(false)}
        footer={[
          <Button key='ok' type='primary' onClick={() => setShowReasonModal(false)}>
            OK
          </Button>
        ]}
        centered
        width={400}
      >
        <Paragraph>Please select an appointment reason before choosing a time slot.</Paragraph>
      </Modal>
    </div>
  )
}

export default DoctorAvailabilityView
